/**
 * View State Preservation Composable
 *
 * Preserves user's view state (filters, view mode, scroll position) across navigation
 * to album edit/detail modals and back. Uses localStorage with 30-minute expiry.
 *
 * Pattern Reference: frontend/src/composables/useAdminAuth.js (Story 1.4)
 */

const STATE_KEY = 'tierlist_view_state';
const EXPIRY_MINUTES = 30;

export function useViewStatePreservation() {
  /**
   * Capture current view state to localStorage
   * @param {Object} currentState - Current view state to preserve
   * @param {string} currentState.viewMode - Current view mode ('sessionView' or 'tierListView')
   * @param {Object} currentState.filters - Active filters
   * @param {string|null} currentState.filters.reviewer - Active reviewer filter (tierlist)
   * @param {string|null} currentState.filters.tier - Active tier filter
   * @param {string|null} currentState.filters.search - Search query (tierlist)
   * @param {string|null} currentState.filters.sessionSearch - Search query (sessions)
   * @param {string|null} currentState.filters.searchMode - Search mode ('auto', 'sessions', 'albums')
   * @param {string|null} currentState.filters.submitter - Submitter filter (tierlist)
   * @param {string|null} currentState.filters.filterType - Filter type ('ratings' or 'submitted')
   */
  const captureViewState = (currentState) => {
    try {
      const stateData = {
        viewMode: currentState.viewMode,
        filters: {
          reviewer: currentState.filters?.reviewer || null,
          tier: currentState.filters?.tier || null,
          search: currentState.filters?.search || null,
          sessionSearch: currentState.filters?.sessionSearch || null,
          searchMode: currentState.filters?.searchMode || null,
          submitter: currentState.filters?.submitter || null,
          filterType: currentState.filters?.filterType || null
        },
        scrollY: window.scrollY,
        timestamp: new Date().toISOString(),
        returnPath: window.location.pathname
      };

      localStorage.setItem(STATE_KEY, JSON.stringify(stateData));
      console.log('[ViewState] State captured:', {
        viewMode: stateData.viewMode,
        scrollY: stateData.scrollY,
        hasFilters: Object.values(stateData.filters).some(v => v !== null)
      });
    } catch (error) {
      console.error('[ViewState] Failed to capture state:', error);
    }
  };

  /**
   * Restore view state from localStorage
   * @returns {Object|null} Restored state or null if expired/missing
   */
  const restoreViewState = () => {
    const storedState = localStorage.getItem(STATE_KEY);
    if (!storedState) {
      console.log('[ViewState] No stored state found');
      return null;
    }

    try {
      const state = JSON.parse(storedState);

      // Validate state structure
      if (!state.timestamp || !state.viewMode) {
        console.warn('[ViewState] Invalid state structure, clearing');
        clearViewState();
        return null;
      }

      // Check expiry
      const stateTime = new Date(state.timestamp).getTime();
      const now = new Date().getTime();
      const expiryTime = stateTime + (EXPIRY_MINUTES * 60 * 1000);

      if (now > expiryTime) {
        console.log('[ViewState] State expired, clearing');
        clearViewState();
        return null;
      }

      console.log('[ViewState] State restored:', {
        viewMode: state.viewMode,
        scrollY: state.scrollY,
        ageMinutes: Math.round((now - stateTime) / 1000 / 60)
      });

      return state;
    } catch (error) {
      console.error('[ViewState] Failed to restore state:', error);
      clearViewState();
      return null;
    }
  };

  /**
   * Clear view state from localStorage
   */
  const clearViewState = () => {
    localStorage.removeItem(STATE_KEY);
    console.log('[ViewState] State cleared');
  };

  return {
    captureViewState,
    restoreViewState,
    clearViewState
  };
}
